/**
 * Session context: who is signed in, what they may do, and whether we are online.
 *
 * Permission checks live here so a component asks `can('refund')` rather than
 * comparing role strings. The backend enforces the same permissions on every
 * route — hiding a button is a courtesy, not the control.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import * as api from './api';
import { flushOutbox, outboxCount, type OutboxEntry } from './offline';

interface SessionValue {
  user: api.User | null;
  /** True until the initial session restore has finished. */
  loading: boolean;
  online: boolean;
  /** Number of writes queued while offline. */
  pending: number;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: string) => boolean;
  /** Retry queued writes now; returns a short human-readable summary. */
  sync: () => Promise<string>;
  refreshPending: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Provides session state to the app. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<api.User | null>(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);

  const refreshPending = useCallback(() => {
    void outboxCount().then(setPending);
  }, []);

  // Restore the session from the HttpOnly refresh cookie on first load.
  useEffect(() => {
    let cancelled = false;
    api.setUnauthenticatedHandler(() => setUser(null));
    void api
      .restoreSession()
      .then((restored) => {
        if (!cancelled) setUser(restored);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    refreshPending();
    return () => {
      cancelled = true;
      api.setUnauthenticatedHandler(null);
    };
  }, [refreshPending]);

  const sync = useCallback(async (): Promise<string> => {
    const result = await flushOutbox(async (entry: OutboxEntry) => {
      await api.request(entry.path, { method: entry.method, body: entry.body });
    });
    refreshPending();
    if (result.sent === 0 && result.failed === 0 && result.dropped === 0) {
      return 'Nothing was waiting to sync.';
    }
    let summary = `Sent ${result.sent}.`;
    if (result.failed) summary += ` ${result.failed} still queued.`;
    if (result.dropped) summary += ` ${result.dropped} discarded after repeated failures.`;
    return summary;
  }, [refreshPending]);

  // Track connectivity and drain the outbox as soon as we are back.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void sync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [sync]);

  const signIn = useCallback(async (email: string, password: string) => {
    setUser(await api.login(email, password));
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  const can = useCallback(
    (permission: string) => Boolean(user?.permissions?.includes(permission)),
    [user],
  );

  const value = useMemo<SessionValue>(
    () => ({ user, loading, online, pending, signIn, signOut, can, sync, refreshPending }),
    [user, loading, online, pending, signIn, signOut, can, sync, refreshPending],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Read the session context. */
export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return context;
}
